import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  Users,
  CalendarDays,
  FileText,
  LogOut,
  Menu,
  X,
  Phone,
  Settings as SettingsIcon,
} from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_7b5007eb-b971-4941-9399-b2d87f9cbac2/artifacts/1lsetayu_barmann%20logo.png";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/schedule", label: "Schedule", icon: CalendarDays, testid: "nav-schedule" },
  { to: "/customers", label: "Customers", icon: Users, testid: "nav-customers" },
  { to: "/invoices", label: "Invoices", icon: FileText, testid: "nav-invoices" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-[#0a0f0d] text-[#eae6db]">
      {/* Sidebar desktop */}
      <aside
        className="hidden md:flex flex-col w-64 bg-[#0a0f0d] border-r border-[#233129] sticky top-0 h-screen"
        data-testid="sidebar"
      >
        <div className="px-5 py-6 border-b border-[#233129] flex items-center gap-3">
          <img src={LOGO_URL} alt="Barmann" className="w-10 h-10 object-contain" />
          <div>
            <div className="font-display font-black text-sm tracking-tight leading-tight">
              BARMANN
            </div>
            <div className="overline text-[10px] text-[#8ba394]">Lawn Service</div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1" data-testid="nav-menu">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors rounded-sm relative ${
                  isActive
                    ? "bg-[#1e3b2d] text-[#eae6db] before:content-[''] before:absolute before:left-0 before:top-2 before:bottom-2 before:w-[3px] before:bg-[#c83e2d]"
                    : "text-[#b0c0b6] hover:bg-[#141c18] hover:text-[#eae6db]"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              <span className="font-medium">{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="px-5 py-4 border-t border-[#233129] text-xs text-[#8ba394] flex items-center gap-2">
          <Phone className="w-3.5 h-3.5" />
          <span className="font-mono">(678) 559-6163</span>
        </div>

        <div className="p-3 border-t border-[#233129]">
          <div className="flex items-center gap-3 px-2 py-2">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-8 h-8 rounded-sm" />
            ) : (
              <div className="w-8 h-8 rounded-sm bg-[#1e3b2d] flex items-center justify-center text-xs font-bold">
                {user?.name?.[0]?.toUpperCase() || "A"}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs text-[#8ba394] truncate">{user?.email}</div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="bm-btn bm-btn-ghost w-full justify-start mt-1"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4" /> <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Mobile topbar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-[#0a0f0d] border-b border-[#233129] flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2">
          <img src={LOGO_URL} alt="Barmann" className="w-8 h-8 object-contain" />
          <span className="font-display font-black text-sm">BARMANN</span>
        </div>
        <button
          onClick={() => setMobileOpen((v) => !v)}
          className="p-2 rounded-sm hover:bg-[#141c18]"
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden fixed top-[57px] left-0 right-0 z-40 bg-[#0a0f0d] border-b border-[#233129] p-3 space-y-1">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              onClick={() => setMobileOpen(false)}
              data-testid={`mobile-${n.testid}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm ${
                  isActive ? "bg-[#1e3b2d]" : "hover:bg-[#141c18]"
                }`
              }
            >
              <n.icon className="w-4 h-4" /> {n.label}
            </NavLink>
          ))}
          <button
            onClick={handleLogout}
            className="bm-btn bm-btn-ghost w-full justify-start"
            data-testid="mobile-logout-button"
          >
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      )}

      <main className="flex-1 min-w-0 pt-16 md:pt-0">
        <div className="p-5 md:p-8 bm-fade-in">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
